import React from 'react';
import type { CSSProperties } from 'react';
import CustomSingleSelect from '../../util_components/custom_single_select';
import { format_talk_time } from './outcome';
import type { CodeLabel } from './outcome';

export type OutcomeMode = 'done' | 'skip';

interface Props {
  mode: OutcomeMode;
  outcomes: CodeLabel[];
  outcome: string;
  on_outcome: (code: string) => void;
  require_outcome: boolean;
  talk_time_seconds: number;
  override_minutes: string;
  on_override: (minutes: string) => void;
  skip_reasons: CodeLabel[];
  skip_reason: string;
  on_skip_reason: (code: string) => void;
  skip_note: string;
  on_skip_note: (note: string) => void;
  disabled?: boolean;
  error?: string;
}

const INPUT: CSSProperties = {
  padding: '2px 6px',
  borderRadius: 6,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  color: 'var(--input-text)'
};

// Mirrors goserver's maxTalkTimeOverrideSeconds.
const MAX_OVERRIDE_MINUTES = 240;

function OutcomePanel({
  mode,
  outcomes,
  outcome,
  on_outcome,
  require_outcome,
  talk_time_seconds,
  override_minutes,
  on_override,
  skip_reasons,
  skip_reason,
  on_skip_reason,
  skip_note,
  on_skip_note,
  disabled = false,
  error = ''
}: Props) {
  return (
    <div
      data-testid="outcome-panel"
      className="rounded-md border px-4 py-3 mt-6"
      style={{ borderColor: 'var(--border-default)', maxWidth: 720 }}
    >
      {mode === 'skip' ? (
        <div className="flex flex-col gap-3">
          <CustomSingleSelect
            id="sales-task-skip-reason"
            label="Skip reason"
            name="skip_reason"
            value={skip_reason}
            defaultOption="Select a reason"
            defaultOptionDisabled={disabled}
            options={skip_reasons.map((r) => ({ value: r.code, label: r.label }))}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => on_skip_reason(e.target.value)}
          />
          <label
            htmlFor="sales-task-skip-note"
            className="flex flex-col gap-1 text-xs"
            style={{ color: 'var(--text-muted)' }}
          >
            Note (optional)
            <textarea
              id="sales-task-skip-note"
              aria-label="Skip note"
              value={skip_note}
              disabled={disabled}
              maxLength={255}
              rows={2}
              onChange={(e) => on_skip_note(e.target.value)}
              style={{ ...INPUT, width: '100%', maxWidth: 480 }}
            />
          </label>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              Outcome
              {require_outcome ? (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (required)</span>
              ) : null}
            </legend>
            <div role="radiogroup" aria-label="Outcome" className="flex flex-wrap gap-4">
              {outcomes.map((o) => (
                <label
                  key={o.code}
                  htmlFor={`sales-task-outcome-${o.code}`}
                  className="inline-flex items-center gap-2 text-sm"
                  style={{ color: 'var(--text-primary)', cursor: disabled ? 'default' : 'pointer' }}
                >
                  <input
                    id={`sales-task-outcome-${o.code}`}
                    type="radio"
                    name="sales-task-outcome"
                    value={o.code}
                    checked={outcome === o.code}
                    disabled={disabled}
                    onChange={() => on_outcome(o.code)}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </fieldset>
          {talk_time_seconds > 0 ? (
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Talk time{' '}
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {format_talk_time(talk_time_seconds)}
              </span>
              , from calls since you claimed
            </div>
          ) : (
            <label
              htmlFor="sales-task-talk-override"
              className="inline-flex items-center gap-2 text-xs"
              style={{ color: 'var(--text-muted)' }}
              title="No calls were logged since you claimed. Enter minutes spent if you talked outside the dialer."
            >
              Talk time (min)
              <input
                id="sales-task-talk-override"
                type="number"
                min={0}
                max={MAX_OVERRIDE_MINUTES}
                step={1}
                value={override_minutes}
                disabled={disabled}
                onChange={(e) => on_override(e.target.value)}
                style={{ ...INPUT, width: 72 }}
              />
            </label>
          )}
        </div>
      )}
      {error ? (
        <div role="alert" className="text-xs mt-2" style={{ color: 'var(--state-error-text)' }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default OutcomePanel;
